import {
  bareTokenName,
  findListBindingName,
  findRepeatableKeyForEntity,
  namespaceListToken,
} from "@features/dynamic-variables/insert/list-namespace";
import { store } from "@store";
import { getRepeatables } from "@store/story/selectors";
import { addRepeatableField } from "@store/story/thunks/repeatable.thunk";

/**
 * Namespaces a token inserted into a text element that lives inside a REPEATABLE
 * group (or a legacy list-bound container): `{{organization_name}}` becomes
 * `{{array_list.organization_name}}`, which is the shape the runtime substitutes
 * per cloned row.
 *
 * Applied HERE because this is the single choke point every Froala insertion
 * goes through — the "(x)" toolbar panel, the `{{` inline typeahead, and a
 * variable dropped onto the active editor all end up in `setDynamicVariable`.
 * It also registers the token as a field of the group, since a group's fields
 * are derived only at mark time and the share dialog needs a column to fill.
 */
export const namespaceInsertedToken = (
  editorId: string,
  token: string,
): string => {
  // A table's cells share ONE Froala keyed `${tableId}-table-froala`
  // (table-cell-froala.tsx); the entity tree only knows the table id.
  const entityId = editorId.replace(/-table-froala$/, "");
  try {
    const state = store.getState();
    const flat = state?.story?.flatContentLayers;
    if (!flat) return token;
    const repeatables = state?.story?.story ? getRepeatables(state) : [];
    const repeatableKey = findRepeatableKeyForEntity(
      flat,
      entityId,
      repeatables,
    );
    const namespace = repeatableKey ?? findListBindingName(flat, entityId);
    if (!namespace) return token;

    const next = namespaceListToken(token, namespace);
    if (repeatableKey && next !== token) {
      store.dispatch(
        addRepeatableField({
          entityId,
          fieldKey: bareTokenName(token),
          name: bareTokenName(token),
          type: "input",
        }) as never,
      );
    }
    return next;
  } catch {
    // Never let namespacing break a plain insert.
    return token;
  }
};
